import Vue from 'vue';
import { Commit } from 'vuex';
import { Zone, ZoneEdit } from '@/interfaces/lighting';
import { Device } from '@/interfaces/locations';

interface ZoneState {
	zones: {
		[id: number]: Zone;
	};
	selectedZone: Zone | null;
}

export default {
	namespaced: true,
	state: {
		zones: [],
		selectedZone: null,
	} as ZoneState,
	getters: {
		getZones: (state: ZoneState): Zone[] => {
			return Object.values(state.zones);
		},

		getRoomZones:
			(state: ZoneState) =>
			(roomId: number): Zone[] => {
				return Object.values(state.zones).filter((zone) => Number(zone.fk_room) == roomId);
			},

		getZone:
			(state: ZoneState) =>
			(id: number): Zone | undefined => {
				return state.zones[id];
			},

		getSelectedZone: (state: ZoneState): Zone | null => {
			return state.selectedZone;
		},

		getDeviceZoneId:
			(state: ZoneState) =>
			(id: number): number | undefined => {
				return Object.values(state.zones).find((zone) => {
					return zone.devices.includes(id);
				})?.id;
			},

		getZoneLuminaires:
			/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,  @typescript-eslint/no-explicit-any */
			(state: ZoneState, getters: any, rootState: any, rootGetters: any) => {
				return (zoneId: number): Zone[] => {
					return (
						getters
							.getZone(zoneId)
							?.devices.map((device: number) => {
								return rootGetters['devices/luminaires/getLuminaire'](device);
							})
							.filter(Boolean) ?? []
					);
				};
			},

		getZoneSwitches:
			/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,  @typescript-eslint/no-explicit-any */
			(state: ZoneState, getters: any, rootState: any, rootGetters: any) => {
				return (zoneId: number): Zone[] => {
					return (
						getters
							.getZone(zoneId)
							?.devices.map((device: number) => {
								return rootGetters['devices/switches/getSwitch'](device);
							})
							.filter(Boolean) ?? []
					);
				};
			},

		getZoneMovementSensors:
			// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,  @typescript-eslint/no-explicit-any
			(state: ZoneState, getters: any, rootState: any, rootGetters: any) => {
				return (zoneId: number): Zone[] => {
					return (
						getters
							.getZone(zoneId)
							?.devices.map((device: number) => {
								return rootGetters['devices/luminaires/getLuminaire'](device);
							})
							.filter(Boolean) ?? []
					).filter((device: Device) => device.is_movement_sensor);
				};
			},
	},
	mutations: {
		SET_ZONES(state: ZoneState, zones: Zone[]): void {
			const ret: { [id: number]: Zone } = {};
			state.zones = zones.reduce((obj, zone) => {
				obj[zone.id] = zone;
				return obj;
			}, ret);
		},

		ADD_ZONE(state: ZoneState, zone: Zone): void {
			Vue.set(state.zones, zone.id, zone);
		},

		CHANGE_ZONE(state: ZoneState, zone: Zone & { id: number }): void {
			const oldZone = state.zones[zone.id] || {};
			Vue.set(state.zones, zone.id, { ...oldZone, ...zone });
		},

		DELETE_ZONE(state: ZoneState, id: number): void {
			Vue.delete(state.zones, id);
		},

		CHANGE_SELECTED_ZONE(state: ZoneState, zone: Zone): void {
			state.selectedZone = zone;
		},

		CLEAN_ZONE(state: ZoneState): void {
			state.zones = {};
		},

		CLEAN_SELECTED_ZONE(state: ZoneState): void {
			state.selectedZone = null;
		},
	},
	actions: {
		async fetchZones({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/lighting/zones`);

			if (response && response.status === 200) {
				commit('SET_ZONES', response.data.data);
			}
		},

		async addZone(
			{ commit }: { commit: Commit },
			{ payload }: { payload: ZoneEdit }
		): Promise<void> {
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/lighting/zones`,
				payload
			);
			if (response && response.status === 201) {
				commit('ADD_ZONE', response.data.data);
			}
		},

		async modifyZone(
			{ commit }: { commit: Commit },
			{ id, payload }: { id: number; payload: ZoneEdit }
		): Promise<void> {
			const response = await Vue.axios.put(
				`${process.env.VUE_APP_API_URL}/lighting/zones/${id}`,
				payload
			);
			if (response && response.status === 200) {
				commit('CHANGE_ZONE', response.data.data);
			}
		},

		async deleteZone({ commit }: { commit: Commit }, { id }: { id: number }): Promise<void> {
			const response = await Vue.axios.delete(
				`${process.env.VUE_APP_API_URL}/lighting/zones/${id}`
			);
			if (response && response.status === 200) {
				commit('DELETE_ZONE', id);
			}
		},

		selectZone({ commit }: { commit: Commit }, { zone }: { zone: Zone }): void {
			if (zone) {
				commit('CHANGE_SELECTED_ZONE', zone);
			}
		},

		cleanZone({ commit }: { commit: Commit }): void {
			commit('CLEAN_ZONE');
		},

		cleanSelectedZone({ commit }: { commit: Commit }): void {
			commit('CLEAN_SELECTED_ZONE');
		},

		cleanAllZone({ commit }: { commit: Commit }): void {
			commit('CLEAN_ZONE');
			commit('CLEAN_SELECTED_ZONE');
		},
	},
};
